import { useEffect, useRef, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useQuery } from "@tanstack/react-query";
import BaseButton from "../../../components/ui/BaseButton";
import AddEditTeamModal from "./components/AddEditTeamModal";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { deleteTeam, fetchTeams } from "../../../services/api/teams";
import { debounce } from "lodash";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import UserProfileLink from "../../../components/ui/UserProfileLink";

const menuOptions = [
  {
    label: "Edit",
    value: "EDIT",
  },
  {
    label: "Delete",
    value: "DELETE",
  },
];

const CompanyTeamsTable = () => {
  const dispatch = useDispatch();
  const [isAddEditTeamOpen, setIsAddEditTeamOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [viewTeam, setViewTeam] = useState(null);
  const { userData } = useCurrentUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);

  const baseQueryParams = {
    limit: 20,
    page: 1,
    search: "",
  };
  const [queryParams, setQueryParams] = useState(baseQueryParams);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [teamsList, setTeamsList] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const {
    data: teamsData,
    isLoading: isTeamsLoading,
    error: teamsError,
    refetch: refetchTeams,
  } = useQuery({
    queryKey: ["teamsData", queryParams.limit, queryParams.page, queryParams.search],
    queryFn: () => fetchTeams(queryParams),
  });
  useEffect(() => {
    if (teamsData?.data?.data) {
      setTeamsList(teamsData.data.data);
      setRowCount(teamsData.data.totalCount);
    }
  }, [teamsData]);

  const columns = [
    {
      field: "name",
      headerName: "Team Name",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <p
          onClick={() => handleItemClick("VIEW", params.row)}
          className="text-primary cursor-pointer"
        >
          {params.value}
        </p>
      ),
    },
    {
      field: "email",
      headerName: "Team Email",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => <p>{params.value || "-"}</p>,
    },
    {
      field: "members",
      headerName: "Members",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        params.value?.length ? (
          <p className="truncate">
            {params.value.map((member, index) => (
              <span key={member.teamMember?.id ?? index}>
                {index > 0 && ", "}
                <UserProfileLink as="span" userId={member.teamMember?.id}>
                  {member.teamMember?.firstName}
                </UserProfileLink>
              </span>
            ))}
          </p>
        ) : (
          <p>No members</p>
        ),
    },
    {
      field: "department",
      headerName: "Department",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => <p>{params.value?.name || "-"}</p>,
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        params.value ? (
          <UserProfileLink userId={params.value.id}>
            {`${params.value.firstName} ${params.value.lastName}`}
          </UserProfileLink>
        ) : (
          <p>N/A</p>
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 90,
      renderCell: (params) => (
        <BaseDropdownV2
          menuOptions={menuOptions}
          triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
          onItemClick={(item) => handleItemClick(item, params.row)}
        />
      ),
    },
  ];
  const updateQueryParamsDebounced = useRef(
    debounce((query) => {
      setQueryParams((prev) => ({
        ...prev,
        search: query.trim() || "",
        page: 1,
      }));
    }, 500),
  ).current;

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateQueryParamsDebounced(query);
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);

    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
  };
  const handleItemClick = (action, team) => {
    setSelectedTeam(team);
    if (action === "EDIT") {
      setIsAddEditTeamOpen(true);
    } else if (action === "DELETE") {
      setIsDeleteConfirmationModalOpen(true);
    } else if (action === "VIEW") {
      setViewTeam(team);
      setIsAddEditTeamOpen(true);
    }
  };
  const handleDeleteTeam = async (teamId) => {
    try {
      await deleteTeam(teamId);
      setIsDeleteConfirmationModalOpen(false);
      dispatch(
        showToast({
          message: "Team deleted successfully",
          type: "success",
        }),
      );
      refetchTeams();
    } catch (error) {
      dispatch(showToast({ message: error?.response?.data?.message, type: "error" }));
    }
  };

  return (
    <>
      <BaseCard elevation={0} className="py-4 px-5 rounded-xl shadow-lg ">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
          <div className="w-full sm:w-2/6">
            <BaseTextField
              placeholder="Search by team name"
              onChange={handleSearchChange}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>
          <BaseButton
            color="primary"
            className="!py-[5px] whitespace-nowrap w-full sm:w-auto"
            onClick={() => {
              setSelectedTeam(null);
              setIsAddEditTeamOpen(true);
            }}
          >
            Add New Team
          </BaseButton>
        </div>
      </BaseCard>

      <BaseDataTableV2
        className="max-h-[calc(100vh-320px)] overflow-auto"
        rowHeight={46}
        columns={columns}
        data={teamsList}
        rowCount={rowCount}
        disableColumnResize={false}
        disableRowSelectionOnClick={true}
        isLoading={isTeamsLoading}
        hideFooter={false}
        pagination
        pageSizeOptions={[10, 20, 50]}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
      />

      {isAddEditTeamOpen && (
        <AddEditTeamModal
          open={isAddEditTeamOpen}
          close={setIsAddEditTeamOpen}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          refetchAllTeams={refetchTeams}
          setViewTeam={setViewTeam}
          viewTeam={viewTeam}
        />
      )}
      {isDeleteConfirmationModalOpen && (
        <BaseConfirmModal
          open={isDeleteConfirmationModalOpen}
          onClose={() => setIsDeleteConfirmationModalOpen(false)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          title="Delete Team"
          description={
            <p>
              Are you sure you want to delete the team
              {
                <span className="text-red-600 font-medium mx-1 capitalize">
                  "{selectedTeam?.name}"
                </span>
              }
              ? This action cannot be undone.
            </p>
          }
          onConfirm={() => handleDeleteTeam(selectedTeam?.id)}
        />
      )}
    </>
  );
};

export default CompanyTeamsTable;
